import * as React from "react";
import { cn } from "@/lib/utils";
import { Logo } from "@/shared/design-system/Logo";
import { siteConfig } from "@/lib/seo/metadata";
import { CtaLink } from "@/shared/ui/cta-link";
import { footerLogo1, footerLogo2 } from "@/lib/assets";
import { InternalLink } from "@/shared/ui/internal-link";
import { ExternalLink } from "@/shared/ui/external-link";
import { Card } from "@/shared/ui/card";

type FooterProps = React.HTMLAttributes<HTMLElement>;

const primaryLinks = [
  { label: "For Clipping Platforms", href: "/for-clipping-platforms", elementId: "footer:clipping-platforms" },
  { label: "For Agencies & Brands", href: "/for-agencies", elementId: "footer:agencies" },
  { label: "Blog", href: "/blog", elementId: "footer:blog" },
] as const;

const policyLinks = [
  {
    label: "Campaign Participation Terms",
    href: "/terms-of-service",
    elementId: "footer:terms-of-service",
  },
  { label: "Privacy Policy", href: "/privacy-policy", elementId: "footer:privacy-policy" },
  {
    label: "How We Keep Botting Out of Clipping",
    href: "/article/antibotting",
    elementId: "footer:antibotting-article",
  },
] as const;

export function Footer({ className, ...props }: FooterProps) {
  return (
    <Card asChild variant="marketing-veil" padding="marketing" className={cn("w-full gap-10", className)}>
      <footer {...props}>
        <div className="flex w-full flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-3">
          <h2 className="text-heading-lg flex-1 text-(--color-text-primary)">
            Interested in partnering with Evangelist?
          </h2>
          <CtaLink
            analyticsElementId="footer:contact"
            href={siteConfig.contact.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full rounded-full md:w-[var(--size-footer-cta)]"
          >
            Contact Us
          </CtaLink>
        </div>

        <div className="h-px w-full bg-(--color-text-primary)" />

        <div className="grid w-full gap-10 md:grid-cols-3 md:gap-4">
          <div className="flex flex-col gap-4">
            {primaryLinks.map(({ label, href, elementId }) => (
              <InternalLink
                key={label}
                analyticsElementId={elementId}
                href={href}
                className="text-body-md-bold text-(--color-text-primary) md:text-body-lg-bold"
              >
                {label}
              </InternalLink>
            ))}
          </div>
          <div className="flex flex-col gap-4">
            {policyLinks.map(({ label, href, elementId }) => (
              <InternalLink
                key={label}
                analyticsElementId={elementId}
                href={href}
                className="text-body-md-bold text-(--color-text-primary) md:text-body-lg-bold"
              >
                {label}
              </InternalLink>
            ))}
          </div>
          <div className="flex flex-col gap-4">
            <Logo className="h-7 w-[141px]" imageSizes="141px" />
            <ExternalLink
              analyticsElementId="footer:contact-email-link"
              href="mailto:contact@evangelist.so"
              className="text-body-lg text-(--color-text-secondary)"
            >
              contact@evangelist.so
            </ExternalLink>
            <p className="text-body-sm text-(--color-text-secondary)">
              Momentist, Inc., 169 Madison Ave #38364, New York, NY 10016, USA © {new Date().getFullYear()} All rights
              reserved.
            </p>
            <div className="flex items-center gap-4">
              <div className="relative h-10 w-10 overflow-hidden">
                <img
                  src={footerLogo1}
                  alt="GDPR Compliance"
                  loading="lazy"
                  className="absolute left-[-6.97%] top-[-6.54%] h-[113.16%] w-[113.16%] max-w-none"
                />
              </div>
              <img
                src={footerLogo2}
                alt="CCPA Compliance"
                className="h-10 w-10 object-cover object-center"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </footer>
    </Card>
  );
}
